import * as React from "react";
import { useReactToPrint } from "react-to-print";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import type { SelectListItem } from "../../../../model/SelectListItem";
import { getCommunes, getPrintData } from "../api/returnInwardApi";
import type { ReturnInwardDetail } from "../api/returnInwardApi";
import { askPrintOptions } from "../components/dialogs";
import ReturnReceiptPrint from "../print/ReturnReceiptPrint";

const PAGE_STYLE = `
@page { size: A4; margin: 7mm 0 7mm 0; }
body { margin: 0; color: #000; }`;

const readUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("userLogin") || "null");
  } catch {
    return null;
  }
};

/** Ngày in dạng 05.10.2026 như trang cũ */
const formatToday = () => {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(today.getDate())}.${pad(today.getMonth() + 1)}.${today.getFullYear()}`;
};

/** Id phường xã mới của phiếu in: đơn có ĐVTN lấy trong attribute3, sau đó id xã mới, rồi id xã cũ */
const pickCommuneId = (model: Record<string, any> | undefined) => {
  if (!model) return "";
  if (model.dvtn) {
    try {
      return String(JSON.parse(model.attribute3 || "null")?.customerCommuneId ?? "");
    } catch {
      return "";
    }
  }
  return String(model.newCommuneId ?? model.cusCommuneId ?? "");
};

interface PrintJob {
  data: ReturnInwardDetail;
  communes: SelectListItem[];
}

/**
 * In phiếu nhập kho đổi trả: hỏi "In dịch vụ đi kèm", lấy dữ liệu in theo id phiếu (chỉ phiếu đã khoá),
 * tra phường xã mới rồi in mẫu ReturnReceiptPrint. Cùng API và mẫu in với trang cũ.
 */
export const useReturnPrint = (sourceType: string) => {
  const componentRef = React.useRef<HTMLDivElement>(null);
  const [job, setJob] = React.useState<PrintJob | null>(null);
  const [printing, setPrinting] = React.useState(false);
  const userInfo = React.useMemo(readUserInfo, []);
  const currentDate = React.useMemo(formatToday, []);

  const handlePrint = useReactToPrint({ contentRef: componentRef, pageStyle: PAGE_STYLE });

  // Dữ liệu đã vào mẫu in (sau một lần render) thì mới in, in xong bỏ dữ liệu
  React.useEffect(() => {
    if (!job) return;
    handlePrint();
    setJob(null);
  }, [job, handlePrint]);

  const print = React.useCallback(async (id: number) => {
    const options = await askPrintOptions();
    if (!options) return;
    setPrinting(true);
    try {
      const res = await getPrintData(id, options.withServices);
      if (!res) return;
      if (!res.success || !res.data) {
        NotificationExtension.Fails(res.message || "Không lấy được dữ liệu in phiếu nhập kho.");
        return;
      }
      const communeId = pickCommuneId(res.data.tblInventoryTransactionModel);
      let communes: SelectListItem[] = [];
      if (communeId) {
        const communeRes = await getCommunes(communeId);
        if (communeRes?.success && Array.isArray(communeRes.data)) communes = communeRes.data;
      }
      setJob({ data: res.data, communes });
    } catch (error) {
      console.error("[ReturnInward] in phiếu lỗi", error);
      NotificationExtension.Fails("In phiếu nhập kho lỗi, vui lòng thử lại.");
    } finally {
      setPrinting(false);
    }
  }, []);

  const printArea = (
    <div style={{ display: "none" }}>
      <ReturnReceiptPrint
        dataPrint={job?.data ?? null}
        typePrint={sourceType}
        userInfo={userInfo}
        currentDate={currentDate}
        componentRef={componentRef}
        dataCommuneMoi={job?.communes ?? []}
      />
    </div>
  );

  return { print, printing, printArea };
};
